<script setup lang="ts">
  import {
    DropdownMenuContent,
    type DropdownMenuContentEmits,
    type DropdownMenuContentProps,
    DropdownMenuPortal,
    injectDropdownMenuRootContext,
    useForwardPropsEmits
  } from 'reka-ui'
  import { computed, type HTMLAttributes } from 'vue'
  import { useDialogZIndex } from '../../../composables/useDialogZIndex'
  import { cn } from '../../../lib/utils'

  const props = withDefaults(
    defineProps<
      DropdownMenuContentProps & {
        class?: HTMLAttributes['class']
        dataTestid?: string
      }
    >(),
    {
      sideOffset: 4
    }
  )
  const emits = defineEmits<DropdownMenuContentEmits>()

  const delegatedProps = computed(() => {
    const { class: _, ...delegated } = props

    return delegated
  })

  const forwarded = useForwardPropsEmits(delegatedProps, emits)

  // Derive the z from the menu's own open state via useDialogZIndex(open), the
  // same path every other overlay uses (Popover/Combobox/Tooltip). The old
  // static counter (`globalZIndex + dialogsOpen + 1`) gave z=51, which fell
  // behind a nested/stacked modal at z>=52; useDialogZIndex resolves to the
  // host modal's z so portal DOM-order keeps the menu on top.
  const rootContext = injectDropdownMenuRootContext()
  const open = computed(() => rootContext.open.value)
  const { zIndex } = useDialogZIndex(open)
</script>

<template>
  <DropdownMenuPortal>
    <DropdownMenuContent
      data-slot="dropdown-menu-content"
      v-bind="forwarded"
      :class="
        cn(
          'bg-popover text-popover-foreground max-h-(--reka-dropdown-menu-content-available-height) origin-(--reka-dropdown-menu-content-transform-origin) min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border p-1 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          props.class
        )
      "
      :data-testid="props.dataTestid"
      :style="{ zIndex }"
    >
      <slot />
    </DropdownMenuContent>
  </DropdownMenuPortal>
</template>
